import { ApiAddVirtualFurnitureParams, ApiCreateOrder, ApiMaintainer, ApiOrder, ApiOrderDetails, ApiOrderFilterParams, ApiOrderNote, ApiOrderNoteParams } from "./interface.api";
import { ApiFileDownloadRequestID, ApiHooksProps } from "../interface";
import { Appointment, Order, OrderDetails } from "@/types";
import { CreateOrderParam, LogoParams, OrderApi, OrderFilterParams, ServiceParam, UpdateOrderParam } from "./interface";
import { mapApiMaintainers, mapApiOrder, mapApiOrderDetails, mapApiOrderNotes, mapCreateOrderParam, mapOrderFilter, mapServiceParam, mapUpdateOrderParam } from "./mappers";
import { useCallback, useState } from "react";

import { ApiWorker } from "../worker/interface.api";
import { request } from "../request";
import { workersApiMapper } from "../worker/mappers";

const hook = ({ setError, setLoading, setSuccess, serverDownloadPath, serverPath, token }: ApiHooksProps): OrderApi => {
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [ordersForInvoice, setOrdersForInvoice] = useState<Order[]>([]);
  const [ordersOuttakes, setOrdersOuttakes] = useState<Order[]>([]);

  const addNote = useCallback(async (orderId: string, content: string) => {
    const path = `${serverPath}/notes/create`;
    const body: ApiOrderNoteParams = {
      Content: content,
      OrderId: orderId,
    }
    request(path, { body, token });
  }, [serverPath, token]);

  const addVirtualFurniture = useCallback(async (orderServiceId: string, content: string) => {
    const path = `${serverPath}/order/add-virtual-furniture/${orderServiceId}`;
    const body: ApiAddVirtualFurnitureParams = {
      Codes: content
    }
    request(path, { body, token });
  }, [serverPath, token]);

  const changeTemplate = async (orderId: string, templateId: number) => {
    const path = `${serverPath}/order/change-template/`;
    const body = {
      'OrderId': orderId,
      'OrderTemplateId': templateId
    };
    setLoading(true);
    await request(path, { body, token });
    setLoading(false);
  };

  const clearOrderDetails = () => setOrderDetails(null);
  const clearOrders = () => setOrders([]);

  const confirmFiles = useCallback(async (orderId: string, body: string[]) => {
    const path = `${serverPath}/order/confirm-files/${orderId}/`;
    request(path, { body, token });
  }, [serverPath, token]);

  const confirmDraftFiles = useCallback(async (orderId: string, body: string[]) => {
    const path = `${serverPath}/order/confirm-draft-files/${orderId}/`;
    request(path, { body, token });
  }, [serverPath, token]);

  const addDraftFiles = useCallback(async (orderId: string, body: string[]) => {
    const path = `${serverPath}/order/add-draft-files/${orderId}/`;
    request(path, { body, token });
  }, [serverPath, token]);

  const createOfferedService = useCallback(async (orderId: string, service: ServiceParam) => {
    const path = `${serverPath}/order/${orderId}/offered-service`;
    const body = mapServiceParam(service)
    request(path, { body, token });
  }, [serverPath, token]);

  const createOrder = useCallback(async (templateId: string, order: CreateOrderParam) => {
    const path = `${serverPath}/order/create${order.isVirtual ? '-virtual' : '' }/${templateId}`;
    const body = mapCreateOrderParam(order);

    setLoading(true);
    try {
      const orderId = await request<ApiCreateOrder, number>(path, { body, token });

      setLoading(false);

      return { orderId: `${orderId}` };
    } catch (error: unknown) {
      if (typeof error === 'string') {
        setError(error);
      }
      else if (error instanceof Error) {
        setError(error.message || '');
      }

      setLoading(false);

      return { orderId: null };
    }
  }, [serverPath, token]);

  const createOrderNote =  useCallback(async (orderServiceId: string, message: string, visibleToCustomer: boolean, sendViaEmail: boolean) => {
    const path = `${serverPath}/order/create-order-note/${orderServiceId}`;
    const body = {
      Mesage: message,
      Status: sendViaEmail ? 1 : 0,
      Type: visibleToCustomer ? 4 : 0
    }

    setLoading(true);

    try {
        await request(path, { body, token});
    }catch (error: unknown) {
      if (typeof error === 'string') {
        setError(error);
      }
      else if (error instanceof Error) {
        setError(error.message || '');
      }
    }
      setLoading(false);
  }, [serverPath, token]);

  const createOrderNoteCustomer =  useCallback(async (orderServiceId: string, message: string) => {
    const path = `${serverPath}/order/create-order-note-customer/${orderServiceId}`;
    const body = {
      Mesage: message
    }

    setLoading(true);

    try {
        await request(path, { body, token});
    }catch (error: unknown) {
      if (typeof error === 'string') {
        setError(error);
      }
      else if (error instanceof Error) {
        setError(error.message || '');
      }
    }
      setLoading(false);
  }, [serverPath, token]);

  const cancelOrder = useCallback(async (id: string) => {
    const path = `${serverPath}/order/cancel/`;
    const body = {
      'OrderId': id,
    };

    setLoading(true);
    try {
      request(path, { body, token });
      setOrderDetails(null);
      setOrders([]);
    } catch (error: unknown) {
      if (typeof error === 'string') {
        setError(error);
      }
      else if (error instanceof Error) {
        setError(error.message || '');
      }
    }
    setLoading(false);
  }, [serverPath, token]);

  const deleteOrder = useCallback(async (id: string) => {
    const path = `${serverPath}/order/delete/`;
    const body = {
      'OrderId': id,
    };

    setLoading(true);
    try {
      request(path, { body, token });
      setOrderDetails(null);
      setOrders([]);
    } catch (error: unknown) {
      if (typeof error === 'string') {
        setError(error);
      }
      else if (error instanceof Error) {
        setError(error.message || '');
      }
    }
    setLoading(false);
  }, [serverPath, token]);

  const downloadOrderFiles = useCallback(async (
    orderId: string,
    orderServiceId: string,
    resolutions: { name: string }[],
    filesIds: string[],
    isDraft: boolean,
    logoParams?: LogoParams
  ) => {
    const path = `${serverPath}/order/download-files/`;

    const resolutionsData: string[] = [];
    for (let i = 0; i < resolutions.length; i++) {
      resolutionsData.push(resolutions[i].name);
    }

    if (!filesIds) {
      filesIds = [];
    }

    const body = {
      LogoParams: logoParams || null,
      OrderId: orderId,
      OrderServiceId: orderServiceId,
      Resolutions: resolutionsData,
      SelectedFiles: filesIds,
    };

    request<typeof body, ApiFileDownloadRequestID>(path, { body, token }).then((res) => {
      const dlPath = `${serverDownloadPath}/order/${isDraft ? "download-draft-archive" : "download-archive"}/${res.FileDownloadRequestID}`;
      window.open(dlPath, '_self');
    });
  }, [serverPath, token]);

  const generateOrder = useCallback(async (templateId: string, quantity: number, applUserId: string) => {
    const body = {
      ApplicationUserId: applUserId,
      OrderTemplateId: templateId,
      Quantity: quantity,
    };
    const path = `${serverPath}/order/generate/`;
    request(path, { body, token });
  }, [serverPath, token]);

  const getNotes = useCallback(async (orderId: string) => {
    const path = `${serverPath}/order/${orderId}/notes`;

    const res = await request<never, ApiOrderNote[]>(path, { method: 'GET', token });

    return mapApiOrderNotes(res);
  }, [serverPath, token]);

  const getDetailedOrderData = useCallback(async (id: string) => {
    const path = `${serverPath}/order/get-details/${id}/`;

    setLoading(true);
    try {
      const orderDetailsResponse = await request<never, ApiOrderDetails>(path, { token });
      const notes = await getNotes(id);

      const mapped = mapApiOrderDetails(orderDetailsResponse, notes);
      setOrderDetails(mapped);
    } catch (error: unknown) {
      if (typeof error === 'string') {
        setError(error);
      }
      else if (error instanceof Error) {
        setError(error.message || '');
      }
    }
    setLoading(false);
  }, [serverPath, token]);

  const getDetailedOrderDataAnon = useCallback(async (id: string) => {
    const path = `${serverPath}/order/get-details-anon/${id}/`;

    setLoading(true);
    try {
      const orderDetailsResponse = await request<never, ApiOrderDetails>(path, { });
      const notes = await getNotes(id);

      setOrderDetails(mapApiOrderDetails(orderDetailsResponse, notes));
    } catch (error: unknown) {
      if (typeof error === 'string') {
        setError(error);
      }
      else if (error instanceof Error) {
        setError(error.message || '');
      }
    }
    setLoading(false);
  }, [serverPath]);

  const findMaintainer = useCallback(async (filter: string) => {
    const path = `${serverPath}/order/find-maintainer/`;
    const res = await request<{ filter: string }, ApiMaintainer[]>(path, { body: { filter }, token });

    return mapApiMaintainers(res);
  }, [serverPath, token]);

  const findWorker = useCallback(async (filter: string) => {
    const path = `${serverPath}/order/find-worker/`;
    const res = await request<{ filter: string }, ApiWorker[]>(path, { body: { filter }, token });

    return workersApiMapper(res)
  }, [serverPath, token]);

  const getAppointments = useCallback(async (from: Date, to: Date) => {
    const path = `${serverPath}/order/get-appointments/`;
    const body = { End: to, Start: from };
    setLoading(true);
    const res = await request<{ End: Date, Start: Date }, Appointment[]>(path, { body, token });
    setLoading(false);

    return res;
  }, [serverPath, token]);

  const getBranchOrders = useCallback(async (filters?: OrderFilterParams) => {
    const path = `${serverPath}/order/branch-orders/`;
    const body = mapOrderFilter(filters);
    const res = await request<ApiOrderFilterParams, ApiOrder[]>(path, { body, token });

    setOrders(mapApiOrder(res));
  }, [serverPath, token]);

  const getInvoiceList = useCallback(async (filters?: OrderFilterParams) => {
    const path = `${serverPath}/order/invoice-list/`;
    const body = mapOrderFilter(filters);
    request(path, { body, token });
  }, [serverPath, token]);

  // const getList = useCallback(async (branches) => {
  //   const body = branches;
  //   const path = `${serverPath}/order/list/`;
  //   request(path, { body, token });
  // }, [serverPath, token]);

  const getOrderImages = useCallback(async (orderId: string) => {
    const path = `${serverPath}/order/order-photos/${orderId}`;
    request(path, { method: 'GET', token });
  }, [serverPath, token]);

  const getOrderList = useCallback(async (filters?: OrderFilterParams, append = false) => {
    const path = `${serverPath}/order/filtered-list/`;
    const body = mapOrderFilter(filters);
    const res = await request<ApiOrderFilterParams, ApiOrder[]>(path, { body, token });

    if(append) {
      setOrders(orders.concat(mapApiOrder(res)));
      console.log("appended: " + orders.length + " final " +  orders.concat(mapApiOrder(res)).length)
    } else {
      setOrders(mapApiOrder(res));
      console.log("simaple: " + mapApiOrder(res).length)
    }
  }, [orders, serverPath, token]);

  const getOrderListOuttakes = useCallback(async (filters?: OrderFilterParams) => {
    const path = `${serverPath}/order/filtered-list-outtakes/`;
    const body = mapOrderFilter(filters);
    const res = await request<ApiOrderFilterParams, ApiOrder[]>(path, { body, token });

    setOrdersOuttakes(mapApiOrder(res));
  }, [serverPath, token]);

  const getOrderListForInvoice = useCallback(async (filters?: OrderFilterParams) => {
    const path = `${serverPath}/order/orders-for-invoice/`;
    const body = mapOrderFilter(filters);
    const res = await request<ApiOrderFilterParams, ApiOrder[]>(path, { body, token });

    setOrdersForInvoice(mapApiOrder(res));
  }, [serverPath, token]);

  const getPriceForService = useCallback(async (orderId: string, service: ServiceParam) => {
    const path = `${serverPath}/order/${orderId}/service/price`;
    const body = mapServiceParam(service)
    request(path, { body, token });
  }, [serverPath, token]);

  const getUnmaintainedList = useCallback(async () => {
    const path = `${serverPath}/order/unmaintained-list/`;

    return await request<never, number>(path, { method: 'POST', token });
  }, [serverPath, token]);

  const getOrderOuttakesCount = useCallback(async () => {
    const path = `${serverPath}/order/outtakes-count/`;

    return await request<never, number>(path, { method: 'GET', token });
  }, [serverPath, token]);

  const markForInvoicing = useCallback(async (orderId: string) => {
    const path = `${serverPath}/order/mark-for-invoice/${orderId}`;
    setLoading(true);
    await request(path, { token });
    setLoading(false);
    setSuccess('Order marked for invoicing');
  }, [serverPath, token]);

  const markServiceAsComplete = useCallback(async (orderServiceId: string) => {
    const path = `${serverPath}/order/mark-as-complete/${orderServiceId}`;
    await request(path, { token });
  }, [serverPath, token]);

  const notifyCustomer = useCallback(async (orderServiceId: string) => {
    const path = `${serverPath}/order/notify-customer/${orderServiceId}`;
    setLoading(true);
    await request(path, { token });
    setLoading(false);
    setSuccess('Customer notified');
  }, [serverPath, token]);

  const notifyCustomerDraft = useCallback(async (orderServiceId: string) => {
    const path = `${serverPath}/order/notify-customer-draft/${orderServiceId}`;
    request(path, { token });
  }, [serverPath, token]);

  const orderAdditionalService = useCallback(async (orderId: string, service: ServiceParam) => {
    const path = `${serverPath}/order/${orderId}/service`;
    const body = mapServiceParam(service)
    request(path, { body, token });
  }, [serverPath, token]);

  const ordersReport = useCallback(async (from: string, to: string) => {
    const path = `${serverPath}/order/orders-report/`;
    const res = await request<never, { FileDownloadRequestID : string }>(path, {token});
    const filePath = `${path}${res.FileDownloadRequestID}/${from}/${to}`;
    window.open(filePath, '_blank');
  }, [serverPath, token]);

  const reAssignWorker = useCallback(async (orderServiceId: string, applicationUserId: string) => {
    const path = `${serverPath}/order/re-assign-worker/`;
    const body = {
      ApplicationUserId: applicationUserId,
      OrderServiceId: orderServiceId,
    };
    await request(path, { body, token });
  }, [serverPath, token]);

  const sendToClient = useCallback(async (orderId: string) => {
    const path = `${serverPath}/order/send-to-client/${orderId}`;
    setLoading(true);
    await request(path, { token });
    setLoading(false);
    setSuccess('Sent to client');
  }, [serverPath, token]);


  const setDetailedOrderData = useCallback(async (order: UpdateOrderParam) => {
    const path = `${serverPath}/order/set-details/`;

    const body = mapUpdateOrderParam(order);

    setLoading(true);
    try {
      const orderDetailsResponse = await request<ApiCreateOrder, ApiOrderDetails>(path, { body, token });
      const notes = await getNotes(`${orderDetailsResponse.OrderId}`);

      setOrderDetails(mapApiOrderDetails(orderDetailsResponse, notes));
      setSuccess('Order details updated');
    } catch (error: unknown) {
      if (typeof error === 'string') {
        setError(error);
      }
      else if (error instanceof Error) {
        setError(error.message || '');
      }
    }
    setLoading(false);
  }, [serverPath, token]);

  const shareOrder = useCallback(async (orderId: string, email: string) => {
    const path = `${serverPath}/order/share-order/${orderId}`;
    const body = {
      Email: email
    };
    setLoading(true);
    await request(path, { body, token });
    setLoading(false);
    setSuccess('Order shared');
  }, [serverPath, token]);

  const updatePhotoCount = useCallback(async (orderId: string, count: number) => {
    const path = `${serverPath}/order/update-photo-count/${orderId}`;
    const body = {
      PhotoCount: count
    };
    setLoading(true);
    await request(path, { body, token });
    setLoading(false);
    setSuccess('Photocount updated');
  }, [serverPath, token]);

  const updateOrderDetails = (details: OrderDetails) => {
    setOrderDetails(details);
  }

  // const systemSuggest = useCallback(async (body) => {
  //   const path = `${serverPath}/order/suggest-worker/`;
  //   request(path, { body, token });
  // }, [serverPath, token]);

  return {
    addDraftFiles,
    addNote,
    addVirtualFurniture,
    cancelOrder,
    changeTemplate,
    clearOrderDetails,
    clearOrders,
    confirmDraftFiles,
    confirmFiles,
    createOfferedService,
    createOrder,
    createOrderNote,
    createOrderNoteCustomer,
    deleteOrder,
    downloadOrderFiles,
    findMaintainer,
    findWorker,
    generateOrder,
    getAppointments,
    getBranchOrders,
    getDetailedOrderData,
    getDetailedOrderDataAnon,
    getInvoiceList,
    // getList,
    getNotes,
    getOrderImages,
    getOrderList,
    getOrderListForInvoice,
    getOrderListOuttakes,
    getOrderOuttakesCount,
    getPriceForService,
    getUnmaintainedList,
    markForInvoicing,
    markServiceAsComplete,
    notifyCustomer,
    notifyCustomerDraft,
    orderAdditionalService,
    orderDetails,
    orders,
    ordersForInvoice,
    ordersOuttakes,
    ordersReport,
    reAssignWorker,
    sendToClient,
    setDetailedOrderData,
    shareOrder,
    updateOrderDetails,
    updatePhotoCount
    // systemSuggest,
  }
}

export default hook;